import { cn } from "@rosette/ts/src/util/cn.js";
import type { CSSProperties } from "react";


/*
 * Range inputs are only styleable through vendor pseudo-elements.
 * Modified from https://toughengineer.github.io/demo/slider-styler.
 * --track is the track's colour, and --sx the width of its progress fill.
 */
const THUMB = cn(
    "[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:size-[1.5em]",
    "[&::-webkit-slider-thumb]:rounded-[0.75em] [&::-webkit-slider-thumb]:bg-focus",
    "[&::-webkit-slider-thumb]:shadow-[0_0_2px_black]",
    "[&::-webkit-slider-thumb]:mt-[calc(max((1em_-_2px)_*_0.5,0px)_-_0.75em)]",
    "[&::-moz-range-thumb]:size-[1.5em] [&::-moz-range-thumb]:rounded-[0.75em]",
    "[&::-moz-range-thumb]:bg-focus [&::-moz-range-thumb]:shadow-[0_0_2px_black]",
);
const TRACK = cn(
    "[--track:var(--background-color-slider-track)] hover:[--track:var(--background-color-slider-track-hover)]",
    "active:[--track:var(--background-color-slider-track)]",
    "[&::-webkit-slider-runnable-track]:h-[1em] [&::-webkit-slider-runnable-track]:rounded-[0.5em]",
    "[&::-webkit-slider-runnable-track]:shadow-[0_0_0.4em_rgba(0,0,0,0.3)]",
    "[&::-moz-range-track]:h-[max(calc(1em_-_2px),0px)] [&::-moz-range-track]:rounded-[0.5em]",
    "[&::-moz-range-track]:shadow-none",
);
const TRACK_PLAIN = cn(
    "[&::-webkit-slider-runnable-track]:bg-(--track)",
    "[&::-moz-range-track]:bg-(--track)",
);
const TRACK_PROGRESS = cn(
    "[&::-webkit-slider-runnable-track]:[background:linear-gradient(var(--background-color-focus),var(--background-color-focus))_0/var(--sx)_100%_no-repeat,var(--track)]",
    "[&::-moz-range-track]:[background:linear-gradient(var(--background-color-focus),var(--background-color-focus))_0/var(--sx)_100%_no-repeat,var(--track)]",
);


interface SliderProps {
    id: string;
    name?: string;
    min: number;
    max: number;
    value: number;
    setValue: (value: number) => void;
    hideProgress?: boolean;
}

export function Slider({ id, name, min, max, value, setValue, hideProgress }: SliderProps) {
    const cssProps: CSSProperties & Record<string, string> = {
        "--min": min.toString(),
        "--max": max.toString(),
        "--value": value.toString(),
        "--sx": `calc(1em + ${(value - min) / (max - min)} * (100% - 2em))`,
    };
    return (
        <div className="flex flex-row items-center justify-center">
            <input
                className={cn(
                    "h-[1.7em] cursor-pointer appearance-none shadow-none focus:outline-none",
                    THUMB,
                    TRACK,
                    hideProgress ? TRACK_PLAIN : TRACK_PROGRESS,
                )}
                style={cssProps}
                type="range"
                id={id}
                name={name}
                min={min}
                max={max}
                value={value}
                onChange={e => setValue(Number(e.target.value))} />
        </div>
    );
}
